import { GoogleMap, Marker } from '@react-google-maps/api';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as ClockIcon } from '../../assets/icons/clock-v2-icon.svg';
import { ReactComponent as NewTabIcon } from '../../assets/icons/tables/new-tab.svg';
import { useAppBranding } from '../../config/branding';
import { useGoogleMaps } from '../../contexts/google-map-context/GoogleMapContent';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import endConsumerApi from '../../utils/api/end-consumer-v2';

import SidePaneModel from './SidePaneModel';
import StationSidePanel from './StationSidePanel';

/**
 * Station media object
 */
interface StationMedia {
  url: string;
}

/**
 * Charging station details
 */
interface ChargingStationDetails {
  _id: string;
  name: string;
  address: string;
  latitude: string;
  longitude: string;
  opensAt: string;
  closesAt: string;
  isAvailable: boolean;
  amenities: string[];
  medias: StationMedia[];
}

/**
 * Favorite station API response item
 */
interface FavoriteStation {
  _id: string;
  chargingStationDetails: ChargingStationDetails;
}

/**
 * Convert lat/lng strings to valid Google map position
 */
const getValidPosition = (lat?: string, lng?: string): google.maps.LatLngLiteral | null => {
  const latitude = Number(lat);
  const longitude = Number(lng);

  if (Number.isNaN(latitude) || Number.isNaN(longitude)) return null;

  return { lat: latitude, lng: longitude };
};

/**
 * Format time to local hh:mm format
 */
const formatTime = (dateString: string): string =>
  new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/**
 * Check if station is currently open
 */
const isStationOpen = (opensAt: string, closesAt: string): boolean => {
  const now = new Date();
  const open = new Date(opensAt);
  const close = new Date(closesAt);

  if (close < open) return now >= open || now <= close;

  return now >= open && now <= close;
};

interface ModelDetailsInterface {
  title?: string;
  content: JSX.Element;
  url?: string;
}

/**
 * Map side panel showing favorite charging stations
 */
function MapSidePanel(): JSX.Element {
  const { t } = useTranslation('sidePanel');
  const { isLoaded } = useGoogleMaps();
  const branding = useAppBranding();
  const dispatch = useAppDispatch();
  const { selectedEndConsumer, endConsumerType } = useAppSelector((state) => state.endConsumerType);
  const navigate = useNavigate();

  const [stations, setStations] = useState<FavoriteStation[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [modelDetails, setModelDetails] = useState<ModelDetailsInterface>({
    title: '',
    content: <div />,
    url: '',
  });

  /**
   * Handle open station details
   */
  const handleOpenStationDetails = (station: ChargingStationDetails) => {
    navigate(`?stationId=${station._id}`);
    setIsOpen(true);
    setModelDetails({
      title: '',
      content: <StationSidePanel fetchIdFrom="queryParams" />,
      url: `/ev-admin/end-consumer/${endConsumerType}/profile/${selectedEndConsumer?.userCustomId}/station/${station._id}/station-dashboard?searchBy=${station.name}`,
    });
  };

  /**
   * Open Station NewTab
   */
  const handleOpenStationNewTab = (station: ChargingStationDetails) => {
    navigate(
      `/ev-admin/end-consumer/${endConsumerType}/profile/${selectedEndConsumer?.userCustomId}/station/${station._id}/station-dashboard?searchBy=${station.name}`,
    );
  };

  /**
   * Function to close modal
   */
  const handleCloseModal = () => {
    setIsOpen(false);
    setModelDetails({ title: '', content: <div /> });
  };

  /**
   * Fetch favorite stations
   */
  const fetchStations = useCallback(
    async (userCustomId: string) => {
      try {
        const response = await endConsumerApi.fetchFavoriteStations(userCustomId);
        setStations(response?.data ?? []);
      } catch (error: unknown) {
        const message =
          error && typeof error === 'object' && 'response' in error
            ? (error as any)?.response?.data?.message
            : 'Something went wrong!';

        dispatch(
          setNotificationMessage({
            status: true,
            message,
            type: 'error',
            code: 0,
          }),
        );
      }
    },
    [dispatch],
  );

  useEffect(() => {
    if (selectedEndConsumer?.userCustomId) {
      fetchStations(selectedEndConsumer.userCustomId);
    }
  }, [fetchStations, selectedEndConsumer]);

  /**
   * Static map options (memoized once)
   */
  const miniMapOptions = useMemo<google.maps.MapOptions>(
    () => ({
      disableDefaultUI: true,
      clickableIcons: false,
      draggable: false,
      zoomControl: false,
      scrollwheel: false,
    }),
    [],
  );

  return (
    <div className="map-side-panel-wrap">
      <SidePaneModel
        open={isOpen}
        title={modelDetails.title}
        content={modelDetails.content}
        closePanel={handleCloseModal}
        url={modelDetails.url}
      />
      <div className="map-side-panel-details">
        {stations.map((item) => {
          const station = item.chargingStationDetails;
          const open = isStationOpen(station.opensAt, station.closesAt);
          const mapCenter = getValidPosition(station.latitude, station.longitude);

          return (
            <div key={station._id} className="each-map-data">
              {/* MINI MAP */}
              {isLoaded && mapCenter && (
                <div className="mini-map">
                  <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={mapCenter}
                    zoom={12}
                    options={miniMapOptions}>
                    <Marker
                      position={mapCenter}
                      icon={{
                        url: branding.icons.stationLocator,
                        scaledSize: new window.google.maps.Size(32, 32),
                      }}
                    />
                  </GoogleMap>
                </div>
              )}

              <p
                className="hyperlink"
                role="presentation"
                onClick={() => handleOpenStationDetails(station)}>
                {station.name}
              </p>
              <p className="address">{station.address}</p>

              <div className="map-time-content">
                <div className="map-time">
                  <p className={open ? 'open' : 'closed'}>{open ? t('open') : t('closed')}</p>
                  <p className="icon">
                    <ClockIcon /> {formatTime(station.opensAt)} - {formatTime(station.closesAt)}
                  </p>
                </div>
                <div
                  className="hyperlink"
                  role="presentation"
                  onClick={() => handleOpenStationNewTab(station)}>
                  <NewTabIcon />{' '}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default MapSidePanel;
